/**
 * LanguageSelector Component
 *
 * TASK-167: Standalone language selector for pre-login use
 *
 * Simple dropdown for selecting language (English, German, French, Italian)
 * Works independently of AccountContext - can be used on login page
 * Uses i18next directly for language changes
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { Language } from '../contexts/LanguageContext';

interface LanguageSelectorProps {
  className?: string;
  showLabel?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  className = '',
  showLabel = true
}) => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as Language;

  const languages: { code: Language; label: string; flag: string }[] = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  ];

  const handleLanguageChange = async (newLanguage: Language) => {
    // Change language in i18next
    await i18n.changeLanguage(newLanguage);

    // Store in localStorage for persistence
    localStorage.setItem('i18nextLng', newLanguage);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <Globe className="w-4 h-4 text-slate-600" />
      )}
      <select
        value={currentLanguage}
        onChange={(e) => handleLanguageChange(e.target.value as Language)}
        className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-sm
                   focus:outline-none focus:ring-2 focus:ring-kielis-navy focus:border-transparent
                   cursor-pointer transition-all"
        aria-label="Select language"
      >
        {languages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default LanguageSelector;
